import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "./SamplingEventTimePickerInput.module.css";

// Custom SamplingEventTimePickerInput component
// Formats inputs to HH:mm format, with validation and formatting
// If input is not in a convertable format, it will be cleared, and an alert will be shown
// If the input is empty, it will be cleared, and the dropdown will be closed
export const SamplingEventTimePickerInput = ({
  value,
  onChange,
  onBlur = () => {},
  disabled,
  className,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Generate time options in minute-by-minute increments for users to select from in the dropdown
  const timeOptions = Array.from({ length: 1440 }, (_, index) => {
    const hours = Math.floor(index / 60);
    const minutes = index % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  });

  // Function to update dropdown position based on input element
  const updateDropdownPosition = () => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Determine if we should show dropdown above or below the input
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = Math.min(200, timeOptions.length * 36); // approximate height

      const position = {
        left: rect.left,
        width: rect.width,
      };

      // If there's not enough space below, position above the input
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        position.top = rect.top - dropdownHeight;
      } else {
        position.top = rect.bottom;
      }

      setDropdownPosition(position);
    }
  };

  // Update position when dropdown is shown
  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();
    }
  }, [showDropdown]);

  // When opening the dropdown (or when the input value changes while open), scroll to the current time
  useEffect(() => {
    if (!showDropdown || !dropdownRef.current) return;
    // Prefer a formatted time; fall back to current input if already HH:mm
    const formatted =
      validateAndFormatTime(inputValue || "") || inputValue || "";
    if (!/^\d{2}:\d{2}$/.test(formatted)) return;
    // Defer to ensure the list has rendered and positioned
    requestAnimationFrame(() => {
      try {
        const node = dropdownRef.current.querySelector(
          `[data-time="${formatted}"]`
        );
        if (node && typeof node.scrollIntoView === "function") {
          node.scrollIntoView({ block: "center" });
        }
      } catch {}
    });
  }, [showDropdown, inputValue]);

  // Add scroll event listener to update position during scrolling
  useEffect(() => {
    if (showDropdown) {
      // Use both window scroll and capturing phase for all scrollable elements
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);

      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [showDropdown]);

  // Handle clicks outside of dropdown so the dropdown closes and the input is cleared
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        onChange("");
      }
    };

    // Add event listeners for both mouse and touch events, for mobile and desktop
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Everything to deal with formatting the input
  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = (timeStr) => {
    const cleaned = timeStr.toLowerCase().trim();
    const match = cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);

    if (!match) return null;
    // Destructure the match array to get hours, minutes, and period (am/pm)
    let [_, hours, minutes = "00", period] = match;
    hours = parseInt(hours);

    // Dealing with hour input greater than 24
    if (hours > 24) {
      return null;
    }

    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;

    // Return the time in 24-hour format, padded to 2 digits if necessary.
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // // Update rounding function to round to nearest 15 minutes
  // const roundToNearest15 = (timeStr) => {
  //   const [hours, minutes] = timeStr.split(":").map(Number);
  //   let totalMinutes = hours * 60 + minutes;

  //   // Round to nearest 15-minute increment
  //   const remainder = totalMinutes % 15;
  //   if (remainder >= 8) {
  //     // Round up if 8 or more minutes past the last 15-minute mark
  //     totalMinutes = totalMinutes + (15 - remainder);
  //   } else {
  //     // Round down if less than 8 minutes past
  //     totalMinutes = totalMinutes - remainder;
  //   }

  //   // Handle case where rounding pushes us into next day
  //   if (totalMinutes === 24 * 60) {
  //     return "00:00";
  //   }

  //   const newHours = Math.floor(totalMinutes / 60);
  //   const newMinutes = totalMinutes % 60;

  //   return `${newHours.toString().padStart(2, "0")}:${newMinutes
  //     .toString()
  //     .padStart(2, "0")}`;
  // };

  // Validate and format time input
  const validateAndFormatTime = (input) => {
    // Remove all non-digits
    const cleaned = input.replace(/[^\d:]/g, "");

    // Handle HHMM format (like "2359" or "425")
    if (!cleaned.includes(":")) {
      if (cleaned.length === 4) {
        const hours = cleaned.slice(0, 2);
        const minutes = cleaned.slice(2);
        return validateAndFormatTime(`${hours}:${minutes}`);
      } else if (cleaned.length === 3) {
        const hours = cleaned.slice(0, 1);
        const minutes = cleaned.slice(1);
        return validateAndFormatTime(`${hours}:${minutes}`);
      }
    }

    // Try parsing as 12-hour format
    const converted = convertTo24Hour(input);
    if (converted) {
      // return roundToNearest15(converted);
      return converted;
    }

    return null;
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleInputBlur = (e) => {
    // Skipping validation if a dropdown value is clicked. This was causing the dropdown values to be overridden.
    if (dropdownRef.current?.contains(document.activeElement)) {
      return;
    }
    setTimeout(() => {
      if (!inputValue) {
        // Handle empty input
        onChange("");
        // Call onBlur with empty value so parent can send to server
        onBlur({ target: { value: "" } });
        setShowDropdown(false);
        return;
      }
      const formattedTime = validateAndFormatTime(inputValue);
      if (!formattedTime) {
        // Invalid time format - clear both input and parent data
        setInputValue("");
        onChange("");
        // Call onBlur with empty value so parent can send to server
        onBlur({ target: { value: "" } });
        setShowDropdown(false);
        alert(
          "Please enter time in HH:mm format (24-hour) or use the dropdown"
        );
        return;
      }

      // Update both the input display and the parent component
      setInputValue(formattedTime);
      onChange(formattedTime);
      // Call onBlur with the formatted value so parent can send to server
      onBlur({ target: { value: formattedTime } });
      setShowDropdown(false);
    }, 0);
  };

  const handleContainerMouseDown = (e) => {
    // If clicking a time option, prevent the blur event
    if (e.target.classList.contains(styles.timeOption)) {
      e.preventDefault();
      const time = e.target.textContent;
      setInputValue(time);
      onChange(time);
      setShowDropdown(false);
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  };

  useEffect(() => {
    // Only update from parent if the field isn't currently focused
    if (document.activeElement !== inputRef.current) {
      setInputValue(value || "");
    }
  }, [value]);

  return (
    <div
      className={styles.timePickerContainer}
      onMouseDown={handleContainerMouseDown}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={() => !disabled && setShowDropdown(true)}
        disabled={disabled}
        className={`${styles.timeInput} ${className || ""}`}
        placeholder="HH:mm"
      />
      {showDropdown &&
        !disabled &&
        ReactDOM.createPortal(
          (() => {
            const formatted =
              validateAndFormatTime(inputValue || "") || inputValue || "";
            const selectedTime = /^\d{2}:\d{2}$/.test(formatted)
              ? formatted
              : "";
            return (
              <ul
                ref={dropdownRef}
                className={styles.timeDropdown}
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }}
                role="listbox"
              >
                {timeOptions.map((time) => {
                  const isActive = selectedTime && time === selectedTime;
                  return (
                    <li
                      key={time}
                      data-time={time}
                      className={`${styles.timeOption} ${
                        isActive ? styles.timeOptionActive : ""
                      }`}
                      aria-selected={isActive ? "true" : "false"}
                      role="option"
                    >
                      {time}
                    </li>
                  );
                })}
              </ul>
            );
          })(),
          document.body
        )}
    </div>
  );
};
